import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useSyncExternalStore,
} from "react";
import { isUserProfileServiceConfigured } from "src/env.js";
import { usePatchProfileMutation } from "src/common/service/userProfile/userProfileApi.js";
import {
	type LocalPreference,
	preferenceStorageKey,
	readLocalPreference,
	subscribeToLocalPreference,
	writeLocalPreference,
} from "src/common/utils/localPreference.js";

const REMOTE_SYNC_DEBOUNCE_MS = 2000;

// Cached by raw string so useSyncExternalStore returns a stable reference when unchanged.
const snapshotCache = new Map<
	string,
	{ raw: string | null; snapshot: LocalPreference<unknown> | undefined }
>();

const readSnapshot = <T>(
	storageKey: string,
): LocalPreference<T> | undefined => {
	let raw: string | null;
	try {
		raw = localStorage.getItem(storageKey);
	} catch {
		raw = null;
	}

	const cached = snapshotCache.get(storageKey);
	if (cached && cached.raw === raw) {
		return cached.snapshot as LocalPreference<T> | undefined;
	}

	const snapshot = readLocalPreference<T>(storageKey);
	snapshotCache.set(storageKey, { raw, snapshot });
	return snapshot;
};

/** Local-first read/write for one preference: writes locally immediately, pushes remotely debounced. */
export const useProfilePreference = <T>(key: string, defaultValue: T) => {
	const [patchProfile] = usePatchProfileMutation();
	const storageKey = useMemo(() => preferenceStorageKey(key), [key]);
	const pendingRef = useRef<
		{ value: T; timer: ReturnType<typeof setTimeout> } | undefined
	>(undefined);

	const entry = useSyncExternalStore(subscribeToLocalPreference, () =>
		readSnapshot<T>(storageKey),
	);
	const value = entry?.value ?? defaultValue;

	const pushRemote = useCallback(
		(next: T) => {
			void patchProfile({ preferences: { [key]: next } })
				.unwrap()
				.catch(() => console.warn("Failed to sync preference", key));
		},
		[key, patchProfile],
	);

	useEffect(() => {
		// Flush a pending debounced write on unmount instead of dropping it.
		return () => {
			const pending = pendingRef.current;
			if (!pending) {
				return;
			}
			clearTimeout(pending.timer);
			pendingRef.current = undefined;
			pushRemote(pending.value);
		};
	}, [pushRemote]);

	const write = useCallback(
		(next: T) => {
			writeLocalPreference(storageKey, next);

			if (!isUserProfileServiceConfigured) {
				return;
			}
			if (pendingRef.current) {
				clearTimeout(pendingRef.current.timer);
			}
			const timer = setTimeout(() => {
				pendingRef.current = undefined;
				pushRemote(next);
			}, REMOTE_SYNC_DEBOUNCE_MS);
			pendingRef.current = { value: next, timer };
		},
		[storageKey, pushRemote],
	);

	return [value, write] as const;
};
